import { getRuntimeEnv } from '@utils/runtime-env.utils'

// Same-origin pathname only — refuses `https://…`, `//host`, `/\host` so a
// phishing URL can't ride in through `?return_to=`. Resolving against our own
// origin (rather than prefix-matching) catches backslash and other tricks the
// browser normalises: `/\evil.com` and `/\\evil.com` collapse to `//evil.com`
// and resolve cross-origin, which the origin comparison rejects.
export const isSafePath = (path: string): boolean => {
  if (!path.startsWith('/')) {
    return false
  }
  try {
    return new URL(path, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

// Cross-origin return targets that are not laundered through Kratos's
// `allowed_return_urls` (e.g. a client-rendered <a href>) must be checked
// against an explicit allowlist or an attacker can phish via `?return_to=`.
// Same-origin paths pass; otherwise only https paretos.com (sub)domains and
// local dev hosts are trusted. The host anchor rejects look-alikes such as
// `evilparetos.com` and `paretos.com.evil.com`.
export const TRUSTED_RETURN_HOST = /^(?:[a-z0-9-]+\.)*paretos\.com$/

// Last resort only. Two products now hand out invitations, so this constant
// can no longer be the answer for everyone — `DEFAULT_RETURN_URL` names the
// right one per deploy, and without it every non-prod environment would send
// people to production.
const FALLBACK_RETURN_URL = 'https://app.paretos.com'

/**
 * Where to send someone whose link carried no usable `return_to`.
 *
 * Read at call time, not module load: `window._env_` is written by the server
 * into the served page, and a module-level const would bake in whatever (if
 * anything) existed when the bundle was evaluated.
 */
export const defaultReturnUrl = (): string => {
  const configured = getRuntimeEnv('DEFAULT_RETURN_URL')
  // Operator config rather than user input, so this guards a typo, not an
  // attack — but it keeps the invariant that everything we navigate to passed
  // the same check.
  return configured && isTrustedReturnTo(configured) ? configured : FALLBACK_RETURN_URL
}

export const isTrustedReturnTo = (raw: string): boolean => {
  if (isSafePath(raw)) {
    return true
  }
  try {
    const url = new URL(raw)
    if (
      url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
    ) {
      return true
    }
    return url.protocol === 'https:' && TRUSTED_RETURN_HOST.test(url.hostname)
  } catch {
    return false
  }
}

// Belt-and-braces guard for `<form action={flow.ui.action}>`: Kratos is
// same-origin today, but a misconfig must not silently POST credentials elsewhere.
export const isSameOriginFormAction = (action: string): boolean => {
  if (isSafePath(action)) {
    return true
  }
  try {
    const url = new URL(action)
    return url.origin === window.location.origin
  } catch {
    return false
  }
}

// Blocks `javascript:` / `data:` / `file:` and nothing else — cross-origin
// allowlisting lives in Kratos's `allowed_return_urls`. Both http and https
// pass: localhost is http and is a valid cockpit during dev. Used in /settings
// because cockpit links cross-origin; /login and /recovery use isSafePath.
export const isNonScriptUrl = (raw: string): boolean => {
  if (isSafePath(raw)) {
    return true
  }
  try {
    const protocol = new URL(raw).protocol.toLowerCase()
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}
